import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  AvatarGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import { 
  Groups as GroupsIcon,
  Add as AddIcon,
  Person as PersonIcon,
  AssignmentInd as AssignmentIndIcon,
  Info as InfoIcon,
  QrCode as QrCodeIcon,
  MoreVert as MoreVertIcon,
  VisibilityOutlined as VisibilityIcon,
  TaskAlt as TaskAltIcon,
  PeopleAlt as PeopleAltIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import TaskGrid from './TaskGrid';
import TasksTable from './TasksTable';
import { taskDataService } from '../../../services/taskDataService';
import type { TeamProjectDto, TeamProjectTaskDto, TeamGroupDto, TeamMemberDto } from '../../../services/taskDataService';
import { getPersonnel as getPersonnelSql } from '../../../services/migration/personnelDataService';
import TeamDetailsModal from '../modals/TeamDetailsModal';
import CreateTeamTaskModal from '../modals/CreateTeamTaskModal';
import TeamTaskDetailModal from '../modals/TeamTaskDetailModal';
import CreateTeamModal from '../modals/CreateTeamModal';

interface TeamTasksProps {
  companyId: string | null;
  statusFilter: string;
  personnelFilter: string;
  taskSearchTerm: string;
  viewMode: 'card' | 'list';
  loading: boolean;
  onDisplayModeChange?: (event: React.MouseEvent<HTMLElement>, newMode: 'tasks' | 'teams' | null) => void;
  displayMode?: 'tasks' | 'teams';
  onCreateTeamTask?: () => void;
}

const TeamTasks: React.FC<TeamTasksProps> = ({
  companyId,
  statusFilter,
  personnelFilter,
  taskSearchTerm,
  viewMode,
  loading,
  onDisplayModeChange,
  displayMode: externalDisplayMode = 'tasks', // varsayılan değer
  onCreateTeamTask
}) => {
  const { t, i18n } = useTranslation('common2');
  
  // Örnek takım görevleri verileri - gerçek uygulamada bu veriler API'den gelecek
  const [teamTasks, setTeamTasks] = useState<any[]>([]);
  const [loadingTeamTasks, setLoadingTeamTasks] = useState(false);
  
  // Ekipler için state ekle
  const [teams, setTeams] = useState<any[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<any | null>(null);
  const [teamDetailsModalOpen, setTeamDetailsModalOpen] = useState(false);
  const [personnel, setPersonnel] = useState<any[]>([]);
  const [loadingPersonnel, setLoadingPersonnel] = useState(false);
  
  // CreateTeamTaskModal için state
  const [createTeamTaskModalOpen, setCreateTeamTaskModalOpen] = useState(false);
  const [taskGroups, setTaskGroups] = useState<any[]>([]);
  
  // Proje Detayları Modalı için state (SQL int id)
  const [projectDetailModalOpen, setProjectDetailModalOpen] = useState(false);
  const [selectedProjectSqlId, setSelectedProjectSqlId] = useState<number | null>(null);
  
  // Aksiyon menüsü ve düzenleme için state (selectedMenuProjectId = UI id = legacyKey/string)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMenuProjectId, setSelectedMenuProjectId] = useState<string | null>(null);
  const [projectToEditSqlId, setProjectToEditSqlId] = useState<number | null>(null);
  const [teamToEditSqlId, setTeamToEditSqlId] = useState<number | null>(null);
  const isMenuOpen = Boolean(anchorEl);

  // Silme onayı modalı için state (SQL int id)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectSqlIdToDelete, setProjectSqlIdToDelete] = useState<number | null>(null);
  const [teamSqlIdToDelete, setTeamSqlIdToDelete] = useState<number | null>(null);
  const [hasAssignedProjects, setHasAssignedProjects] = useState(false);

  // CreateTeamModal için state
  const [createTeamModalOpen, setCreateTeamModalOpen] = useState(false);
  // Mutasyon sonrası yeniden yükleme tetikleyicisi
  const [refreshNonce, setRefreshNonce] = useState(0);
  const triggerRefresh = () => setRefreshNonce((n) => n + 1);

  // Snackbar için state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' } | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, projectId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedMenuProjectId(projectId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuProjectId(null);
  };

  const handleEditProject = () => {
    if (!selectedMenuProjectId) return;
    const proj = teamTasks.find((p) => p.id === selectedMenuProjectId);
    if (proj?.sqlId) {
      setProjectToEditSqlId(proj.sqlId);
      setCreateTeamTaskModalOpen(true);
    }
    handleMenuClose();
  };

  const handleEditTeam = () => {
    if (!selectedMenuProjectId) return;
    const team = teams.find((tm) => tm.id === selectedMenuProjectId);
    if (team?.sqlId) {
      setTeamToEditSqlId(team.sqlId);
      setCreateTeamModalOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteProject = () => {
    if (!selectedMenuProjectId) return;
    const proj = teamTasks.find((p) => p.id === selectedMenuProjectId);
    setProjectSqlIdToDelete(proj?.sqlId ?? null);
    setDeleteConfirmOpen(true);
    handleMenuClose();
  };

  const handleDeleteTeam = async () => {
    if (!selectedMenuProjectId) return;
    const team = teams.find((tm) => tm.id === selectedMenuProjectId);
    setTeamSqlIdToDelete(team?.sqlId ?? null);

    // Ekibe atanmış projeleri kontrol et (yüklenmiş projeler üzerinden; SQL teamGroupId)
    const hasProjects = teamTasks.some((project) => project.teamId === selectedMenuProjectId);
    setHasAssignedProjects(hasProjects);
    setDeleteConfirmOpen(true);

    handleMenuClose();
  };

  const confirmDeleteProject = async () => {
    // Ekip silme işlemi ise ve atanmış projeler varsa işlemi engelle
    if (teamSqlIdToDelete && hasAssignedProjects) {
      setDeleteConfirmOpen(false);
      setTeamSqlIdToDelete(null);
      setHasAssignedProjects(false);
      return;
    }

    // Proje silme işlemi
    if (projectSqlIdToDelete) {
      try {
        await taskDataService.deleteTeamProject(projectSqlIdToDelete);
        setSnackbar({ open: true, message: t('teamTasks.notifications.deleteSuccess'), severity: 'success' });
        triggerRefresh();
      } catch (error) {
        console.error(`[Delete Project] Error deleting project SQL ID: ${projectSqlIdToDelete}`, error);
        setSnackbar({ open: true, message: t('teamTasks.notifications.deleteError', { error }), severity: 'error' });
      } finally {
        setDeleteConfirmOpen(false);
        setProjectSqlIdToDelete(null);
      }
    }

    // Ekip silme işlemi
    if (teamSqlIdToDelete && !hasAssignedProjects) {
      try {
        await taskDataService.deleteTeamGroup(teamSqlIdToDelete);
        setSnackbar({ open: true, message: t('teamTasks.notifications.teamDeleteSuccess'), severity: 'success' });
        triggerRefresh();
      } catch (error) {
        console.error(`[Delete Team] Error deleting team SQL ID: ${teamSqlIdToDelete}`, error);
        setSnackbar({ open: true, message: t('teamTasks.notifications.deleteError', { error }), severity: 'error' });
      } finally {
        setDeleteConfirmOpen(false);
        setTeamSqlIdToDelete(null);
      }
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(null);
  };
  
  const handleCloseCreateModal = () => {
    setCreateTeamTaskModalOpen(false);
    setProjectToEditSqlId(null); // Düzenleme modundan çıkıldığında ID'yi temizle
  };

  // Takım projelerini tanımlamak için arayüz
  interface TeamTaskProject {
    id: string;          // UI id = legacyKey ?? String(sqlId)
    sqlId: number;       // SQL int (yazma/silme için)
    name: string;
    teamId: string;      // ekip UI id'si
    teamName: string;
    createdAt: number;
    isSequential: boolean;
    status: string;
    tasks: Record<string, any>;
    groupId?: number | null;
    groupName?: string;
    branchId?: string;
    branchName?: string;
    activeTaskOrder?: number | null;
    totalTasks: number;
    completedTasks: number;
    progress: number;
    maxPriority: string;
  }

  // Projeden en yüksek öncelikli görevi bul 
  const getHighestPriority = (tasks: Record<string, any>): string => {
    if (!tasks || Object.keys(tasks).length === 0) return 'low';
    if (Object.values(tasks).some(task => task.priority === 'high')) return 'high';
    if (Object.values(tasks).some(task => task.priority === 'medium')) return 'medium';
    return 'low';
  };

  // Faz 5 DILIM 5: Ekip projeleri/ekipler/personel/grup verileri SQL'den (taskDataService).
  useEffect(() => {
    if (!companyId) return;

    let cancelled = false;
    setLoadingTeamTasks(true);
    setLoadingTeams(true);
    setLoadingPersonnel(true);

    const fetchAllProjects = async (): Promise<TeamProjectDto[]> => {
      const acc: TeamProjectDto[] = [];
      let page = 1;
      for (;;) {
        const res = await taskDataService.listTeamProjects({ page, pageSize: 200 });
        acc.push(...res.items);
        if (res.items.length === 0 || acc.length >= (res.total || 0)) break;
        page++;
      }
      return acc;
    };

    const load = async () => {
      try {
        const [projects, allTasks, teamGroups, allMembers, sqlPersonnel, groupsLegacy] = await Promise.all([
          fetchAllProjects(),
          taskDataService.listAllTeamProjectTasks(),
          taskDataService.listTeamGroups(),
          taskDataService.listAllTeamMembers(),
          getPersonnelSql(companyId),
          taskDataService.listGroupsLegacy(),
        ]);
        if (cancelled) return;

        // Ekip UI id haritası (sqlId → legacyKey/string)
        const teamUiIdBySql: Record<number, string> = {};
        teamGroups.forEach((g: TeamGroupDto) => {
          teamUiIdBySql[g.id] = g.legacyFirebaseKey || String(g.id);
        });

        // Üyeleri ekip bazında grupla (RTDB-uyumlu members map: uid → {name,memberRole,duty,id})
        const membersByTeam: Record<number, Record<string, any>> = {};
        allMembers.forEach((m: TeamMemberDto) => {
          const map = (membersByTeam[m.teamGroupId] = membersByTeam[m.teamGroupId] || {});
          map[m.personnelFirebaseUid] = {
            id: m.id,
            name: m.name,
            duty: m.duty,
            memberRole: m.memberRole,
            personnelFirebaseUid: m.personnelFirebaseUid,
          };
        });

        // Görevleri proje bazında grupla (RTDB-uyumlu tasks map)
        const tasksByProject: Record<number, Record<string, any>> = {};
        allTasks.forEach((tk: TeamProjectTaskDto) => {
          const key = tk.legacyFirebaseKey || String(tk.id);
          const map = (tasksByProject[tk.teamProjectId] = tasksByProject[tk.teamProjectId] || {});
          map[key] = {
            ...tk,
            id: key,
            sqlId: tk.id,
            teamMemberId: tk.teamMemberUid, // eski alan adı (filtre uyumu)
          };
        });

        // Ekipler
        const teamsArray = teamGroups.map((g: TeamGroupDto) => ({
          id: g.legacyFirebaseKey || String(g.id),
          sqlId: g.id,
          name: g.name,
          description: g.description,
          color: g.color,
          createdAt: g.createdAt ? new Date(g.createdAt).getTime() : Date.now(),
          members: membersByTeam[g.id] || {},
        }));
        setTeams(teamsArray);

        // Projeler
        const projectsArray: TeamTaskProject[] = projects.map((p: TeamProjectDto) => {
          const tasks = tasksByProject[p.id] || {};
          const totalTasks = p.taskTotal ?? Object.keys(tasks).length;
          const completedTasks = p.taskCompleted ?? Object.values(tasks).filter((tk: any) => tk.status === 'completed').length;
          const progress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
          return {
            id: p.legacyFirebaseKey || String(p.id),
            sqlId: p.id,
            name: p.name,
            teamId: p.teamGroupId != null ? (teamUiIdBySql[p.teamGroupId] || String(p.teamGroupId)) : '',
            teamName: p.teamName || '',
            createdAt: p.createdAt ? new Date(p.createdAt).getTime() : Date.now(),
            isSequential: p.isSequential,
            status: p.status,
            tasks,
            groupId: p.groupId,
            groupName: p.groupName || undefined,
            branchId: p.branchId || undefined,
            branchName: p.branchName || undefined,
            activeTaskOrder: p.activeTaskOrder,
            totalTasks,
            completedTasks,
            progress,
            maxPriority: getHighestPriority(tasks),
          };
        });
        projectsArray.sort((a, b) => b.createdAt - a.createdAt);
        setTeamTasks(projectsArray);

        // Personel (id=firebaseUid, name) — getPersonnel zaten silinmişleri hariç tutar
        const personnelArray = (sqlPersonnel || []).map((p: any) => ({
          id: p.id,
          name: p.name || 'İsimsiz Personel',
          email: p.email || '',
          phone: p.phone || '',
          role: p.role || 'employee',
        }));
        setPersonnel(personnelArray);

        // Görev grupları (RTDB-uyumlu; id=legacyKey, sqlId taşır)
        setTaskGroups(Array.isArray(groupsLegacy) ? groupsLegacy : []);
      } catch (error) {
        if (!cancelled) {
          console.error('Ekip verileri yüklenirken hata:', error);
          setTeamTasks([]);
          setTeams([]);
          setPersonnel([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingTeamTasks(false);
          setLoadingTeams(false);
          setLoadingPersonnel(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [companyId, refreshNonce]);
  
  // Verileri filtrele
  const filteredTasks = useMemo(() => {
    if (!teamTasks) return [];
    
    return teamTasks.filter(project => {
      // Durum filtresini uygula
      if (statusFilter !== 'all') {
        if (statusFilter === 'completed' && project.status !== 'completed') return false;
        if (statusFilter === 'pending' && project.status === 'completed') return false; // 'pending' filtresi tamamlanmamış tüm projeler
      }
      
      // Arama filtresi
      if (taskSearchTerm) {
        const searchTermLower = taskSearchTerm.toLowerCase();
        const nameMatch = project.name.toLowerCase().includes(searchTermLower);
        const teamNameMatch = project.teamName.toLowerCase().includes(searchTermLower);
        
        if (!nameMatch && !teamNameMatch) return false;
      }
      
      // Personel filtresi - projedeki herhangi bir görevde ilgili personel var mı kontrol et
      if (personnelFilter !== 'all') {
        const hasPersonnel = Object.values(project.tasks || {}).some((task: any) => 
          task.teamMemberId === personnelFilter
        );
        if (!hasPersonnel) return false;
      }
      
      return true;
    });
  }, [teamTasks, statusFilter, taskSearchTerm, personnelFilter]);
  
  // Görev tipini getir
  const getTaskTypeLabel = (isSequential: boolean) => {
    return isSequential ? t('teamTasks.taskType.sequential') : t('teamTasks.taskType.normal');
  };

  // Görev tipi rengini belirle
  const getTaskTypeColor = (isSequential: boolean) => {
    return isSequential ? '#673AB7' : '#009688'; // Mor: Sıralı, Turkuaz: Normal
  };
  
  // Öncelik rengini belirle
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return '#F44336'; // Kırmızı
      case 'medium':
        return '#FF9800'; // Turuncu
      case 'low':
        return '#4CAF50'; // Yeşil
      default:
        return '#9E9E9E'; // Gri
    }
  };
  
  // Öncelik metni
  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'high':
        return t('teamTasks.priority.high');
      case 'medium':
        return t('teamTasks.priority.medium');
      case 'low':
        return t('teamTasks.priority.low');
      default:
        return t('teamTasks.priority.unspecified');
    }
  };
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Sayfa değişikliği
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Sayfa başına satır değişikliği
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Sayfalanmış veriler
  const paginatedTasks = useMemo(() => {
    return filteredTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredTasks, page, rowsPerPage]);
  
  // Sayfalanmış ekipler
  const paginatedTeams = useMemo(() => {
    return teams.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [teams, page, rowsPerPage]);
  
  // Ekip detayları modalını aç
  const handleOpenTeamDetails = (team: any) => {
    setSelectedTeam(team);
    setTeamDetailsModalOpen(true);
  };
  
  // Görünüm değiştirme işlevi
  const handleDisplayModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: 'tasks' | 'teams' | null
  ) => {
    if (newMode !== null && onDisplayModeChange) {
      onDisplayModeChange(event, newMode);
    }
  };
  
  // Ekip görevi ekleme modalını açma handler'ı
  const handleOpenCreateTeamTaskModal = () => {
    setCreateTeamTaskModalOpen(true);
  };
  
  // Ekip oluşturma modalını açma handler'ı
  const handleOpenCreateTeamModal = () => {
    setTeamToEditSqlId(null); // Yeni ekip oluşturma modu
    setCreateTeamModalOpen(true);
  };
  
  // Proje detayları modalını açma (UI id → SQL id)
  const handleOpenProjectDetails = (projectUiId: string) => {
    const proj = teamTasks.find((p) => p.id === projectUiId);
    setSelectedProjectSqlId(proj?.sqlId ?? null);
    setProjectDetailModalOpen(true);
  };
  
  // Tarih formatını dile göre ayarla
  const formatDate = (date: number | string | Date) => {
    const dateObj = new Date(date);
    return dateObj.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'tr-TR');
  };
  
  if ((loading || loadingTeamTasks) && externalDisplayMode === 'tasks') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {t('teamTasks.loading')}
        </Typography>
      </Box>
    );
  }
  
  if (loadingTeams && externalDisplayMode === 'teams') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
          {t('teamTasks.teamsLoading')}
        </Typography>
      </Box>
    );
  }
  
  return (
    <Box sx={{ mt: 2, mb: 4 }}>
      {externalDisplayMode === 'tasks' ? (
        // Görevler Görünümü (Mevcut kod)
        <>
          {filteredTasks.length === 0 ? (
            <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ textAlign: 'center', my: 8 }}>
                <GroupsIcon sx={{ fontSize: 80, color: 'primary.light', mb: 2 }} />
                <Typography variant="h5" color="textSecondary" gutterBottom>
                  {t('teamTasks.emptyTasks.title')}
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                  {statusFilter !== 'all' || taskSearchTerm
                    ? t('teamTasks.emptyTasks.noTasksFiltered')
                    : t('teamTasks.emptyTasks.noTasks')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                  onClick={onCreateTeamTask || handleOpenCreateTeamTaskModal}
                >
                  {t('teamTasks.emptyTasks.addTask')}
                </Button>
              </Box>
            </Paper>
          ) : viewMode === 'card' ? (
            // Kart Görünümü
            <>
              <Grid container spacing={2}>
                {paginatedTasks.map((project) => (
                  <Grid item xs={12} sm={6} md={4} key={project.id}>
                    <Card sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(0,0,0,0.1)'
                      }
                    }}>
                      <Box sx={{ 
                        p: 0.5, 
                        height: 8, 
                        bgcolor: getTaskTypeColor(project.isSequential), 
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8
                      }} />
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" component="h2" fontWeight="bold">
                            {project.name}
                          </Typography>
                          <Chip 
                            label={getPriorityLabel(project.maxPriority)} 
                            size="small"
                            sx={{ 
                              bgcolor: `${getPriorityColor(project.maxPriority)}20`,
                              color: getPriorityColor(project.maxPriority),
                              fontWeight: 'medium'
                            }}
                          />
                        </Box>
                        
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              width: 28, 
                              height: 28, 
                              mr: 1, 
                              bgcolor: 'primary.main',
                              fontSize: '0.75rem'
                            }}
                          >
                            <GroupsIcon sx={{ fontSize: '1rem' }} />
                          </Avatar>
                          <Typography variant="body2">
                            {project.teamName}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mb: 2 }}>
                          <Chip 
                            label={getTaskTypeLabel(project.isSequential)} 
                            size="small"
                            sx={{ 
                              mb: 1,
                              bgcolor: `${getTaskTypeColor(project.isSequential)}20`,
                              color: getTaskTypeColor(project.isSequential),
                              fontWeight: 'medium'
                            }}
                          />
                          
                          {(project.groupName || project.branchName) && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              {project.groupName && `Grup: ${project.groupName}`} 
                              {project.groupName && project.branchName && ' | '}
                              {project.branchName && `Şube: ${project.branchName}`}
                            </Typography>
                          )}
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" gutterBottom>
                            {t('teamTasks.cardInfo.tasks')}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body2">
                                {project.completedTasks} / {project.totalTasks} {t('teamTasks.cardInfo.completed')}
                              </Typography>
                              <Box 
                                sx={{ 
                                  mt: 0.5,
                                  height: 8, 
                                  bgcolor: 'grey.200', 
                                  borderRadius: 4,
                                  position: 'relative',
                                  overflow: 'hidden'
                                }}
                              >
                                <Box 
                                  sx={{ 
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    height: '100%',
                                    width: `${project.progress}%`,
                                    bgcolor: project.progress === 100 ? 'success.main' : 'primary.main',
                                    borderRadius: 4 
                                  }}
                                />
                              </Box>
                            </Box>
                            <Typography variant="h6" color={project.progress === 100 ? 'success.main' : 'primary.main'} sx={{ ml: 1, fontWeight: 'bold' }}>
                              {project.progress}%
                            </Typography>
                          </Box>
                        </Box>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('teamTasks.cardInfo.createdAt')} {formatDate(project.createdAt)}
                          </Typography>
                          <Box>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenProjectDetails(project.id)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={(e) => handleMenuOpen(e, project.id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <TablePagination
                component="div"
                count={filteredTasks.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('teamTasks.pagination.rowsPerPageProject')}
                labelDisplayedRows={({ from, to, count }) => 
                  t('teamTasks.pagination.displayedRowsProject', {
                    count: count,
                    from: from,
                    to: to
                  })}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            // Tablo Görünümü
            <>
              <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.50' }}>
                      <TableCell>{t('teamTasks.table.projectName')}</TableCell>
                      <TableCell>{t('teamTasks.table.team')}</TableCell>
                      <TableCell>{t('teamTasks.table.taskType')}</TableCell>
                      <TableCell>{t('teamTasks.table.taskPriority')}</TableCell>
                      <TableCell>{t('teamTasks.table.totalTasks')}</TableCell>
                      <TableCell>{t('teamTasks.table.progress')}</TableCell>
                      <TableCell align="right">{t('teamTasks.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTasks.map((project) => (
                      <TableRow key={project.id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {project.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {project.groupName && `${project.groupName} - `}
                            {project.branchName && `${project.branchName}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ width: 26, height: 26, mr: 1, fontSize: '0.75rem' }}>
                              <GroupsIcon sx={{ fontSize: '1rem' }} />
                            </Avatar>
                            <Typography variant="body2">
                              {project.teamName}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getTaskTypeLabel(project.isSequential)} 
                            size="small"
                            sx={{ 
                              bgcolor: `${getTaskTypeColor(project.isSequential)}20`,
                              color: getTaskTypeColor(project.isSequential),
                              fontWeight: 'medium'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={getPriorityLabel(project.maxPriority)} 
                            size="small"
                            sx={{ 
                              bgcolor: `${getPriorityColor(project.maxPriority)}20`,
                              color: getPriorityColor(project.maxPriority),
                              fontWeight: 'medium'
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="body2">
                              {project.completedTasks} / {project.totalTasks}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              sx={{ 
                                width: 100, 
                                height: 8, 
                                bgcolor: 'grey.200', 
                                borderRadius: 4,
                                mr: 1,
                                position: 'relative',
                                overflow: 'hidden'
                              }}
                            >
                              <Box 
                                sx={{ 
                                  position: 'absolute',
                                  left: 0,
                                  top: 0,
                                  height: '100%',
                                  width: `${project.progress}%`,
                                  bgcolor: project.progress === 100 ? 'success.main' : 'primary.main',
                                  borderRadius: 4 
                                }}
                              />
                            </Box>
                            <Typography variant="caption">{project.progress}%</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenProjectDetails(project.id)}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => handleMenuOpen(e, project.id)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={filteredTasks.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('teamTasks.pagination.rowsPerPageProject')}
                labelDisplayedRows={({ from, to, count }) => 
                  t('teamTasks.pagination.displayedRowsProject', {
                    count: count,
                    from: from,
                    to: to
                  })}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </>
      ) : (
        // Ekipler Görünümü
        <>
          {teams.length === 0 ? (
            <Paper sx={{ p: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ textAlign: 'center', my: 8 }}>
                <PeopleAltIcon sx={{ fontSize: 80, color: 'primary.light', mb: 2 }} />
                <Typography variant="h5" color="textSecondary" gutterBottom>
                  {t('teamTasks.emptyTeams.title')}
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                  {t('teamTasks.emptyTeams.noTeams')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{ mt: 2 }}
                  onClick={handleOpenCreateTeamModal}
                >
                  {t('teamTasks.emptyTeams.createTeam')}
                </Button>
              </Box>
            </Paper>
          ) : viewMode === 'card' ? (
            // Ekipler Kart Görünümü
            <>
              <Grid container spacing={2}>
                {paginatedTeams.map((team) => (
                  <Grid item xs={12} sm={6} md={4} key={team.id}>
                    <Card sx={{ 
                      height: '100%', 
                      display: 'flex', 
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleOpenTeamDetails(team)}
                    >
                      <Box sx={{ 
                        p: 0.5, 
                        height: 8, 
                        bgcolor: team.color || '#1976D2', 
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8
                      }} />
                      <CardContent sx={{ flexGrow: 1, p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6" component="h2" fontWeight="bold">
                            {team.name}
                          </Typography>
                          <Avatar sx={{ bgcolor: team.color || '#1976D2' }}>
                            <GroupsIcon />
                          </Avatar>
                        </Box>
                        
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            mb: 2, 
                            height: 60, 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical'
                          }}
                        >
                          {team.description || t('teamTasks.cardInfo.noDescription')}
                        </Typography>
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          {t('teamTasks.table.members')}:
                        </Typography>
                        
                        {team.members && Object.keys(team.members).length > 0 ? (
                          <>
                            <AvatarGroup max={5} sx={{ mb: 1, justifyContent: 'flex-start' }}>
                              {Object.entries(team.members).map(([memberId, memberData]: [string, any]) => (
                                <Tooltip title={`${memberData.name} (${memberData.memberRole})`} key={memberId}>
                                  <Avatar sx={{ width: 30, height: 30, bgcolor: team.color, fontSize: '0.75rem' }}>
                                    {memberData.name ? memberData.name[0] : '?'}
                                  </Avatar>
                                </Tooltip>
                              ))}
                            </AvatarGroup>
                            
                            <Box sx={{ mb: 1 }}>
                              <Typography variant="body2">
                                {Object.keys(team.members).length} {t('teamTasks.cardInfo.members')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {Object.values(team.members).filter((m: any) => m.memberRole === t('teamTasks.teamRoles.leader')).length} {t('teamTasks.teamRoles.leader')}, 
                                {Object.values(team.members).filter((m: any) => m.memberRole === t('teamTasks.teamRoles.member')).length} {t('teamTasks.teamRoles.member')}, 
                                {Object.values(team.members).filter((m: any) => m.memberRole === t('teamTasks.teamRoles.observer')).length} {t('teamTasks.teamRoles.observer')}
                              </Typography>
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {t('teamTasks.cardInfo.noMembers')}
                          </Typography>
                        )}
                        
                        <Divider sx={{ my: 1.5 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('teamTasks.cardInfo.createdAt')} {formatDate(team.createdAt)}
                          </Typography>
                          <Box>
                            <IconButton size="small" color="primary" onClick={() => handleOpenTeamDetails(team)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={(e) => handleMenuOpen(e, team.id)}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <TablePagination
                component="div"
                count={teams.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('teamTasks.pagination.rowsPerPageTeam')}
                labelDisplayedRows={({ from, to, count }) => 
                  t('teamTasks.pagination.displayedRowsTeam', {
                    count: count,
                    from: from,
                    to: to
                  })}
                sx={{ mt: 2 }}
              />
            </>
          ) : (
            // Ekipler Tablo Görünümü
            <>
              <TableContainer component={Paper} sx={{ boxShadow: '0 4px 12px rgba(0,0,0,0.05)', borderRadius: 2 }}>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: 'primary.50' }}>
                      <TableCell>{t('teamTasks.table.teamName')}</TableCell>
                      <TableCell>{t('teamTasks.table.color')}</TableCell>
                      <TableCell>{t('teamTasks.table.description')}</TableCell>
                      <TableCell>{t('teamTasks.table.members')}</TableCell>
                      <TableCell>{t('teamTasks.table.roles')}</TableCell>
                      <TableCell>{t('teamTasks.table.createdAt')}</TableCell>
                      <TableCell align="right">{t('teamTasks.table.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTeams.map((team) => (
                      <TableRow key={team.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ bgcolor: team.color, width: 30, height: 30, mr: 1 }}>
                              <GroupsIcon sx={{ fontSize: '1rem' }} />
                            </Avatar>
                            <Typography variant="body2" fontWeight="medium">
                              {team.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            width: 24, 
                            height: 24, 
                            borderRadius: '50%', 
                            bgcolor: team.color || '#1976D2',
                            border: '1px solid rgba(0,0,0,0.1)'
                          }} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {team.description && team.description.length > 50 
                              ? `${team.description.substring(0, 50)}...` 
                              : team.description || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {team.members && Object.keys(team.members).length > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <AvatarGroup max={3} sx={{ mr: 1 }}>
                                {Object.entries(team.members).slice(0, 3).map(([memberId, memberData]: [string, any]) => (
                                  <Avatar 
                                    key={memberId} 
                                    sx={{ width: 24, height: 24, fontSize: '0.75rem' }}
                                  >
                                    {memberData.name ? memberData.name[0] : '?'}
                                  </Avatar>
                                ))}
                              </AvatarGroup>
                              <Typography variant="body2">
                                {Object.keys(team.members).length} üye
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {team.members && Object.keys(team.members).length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {Object.values(team.members).filter((m: any) => m.memberRole === 'Lider').length > 0 && (
                                <Chip 
                                  label={`${Object.values(team.members).filter((m: any) => m.memberRole === 'Lider').length} Lider`} 
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                              {Object.values(team.members).filter((m: any) => m.memberRole === 'Görevli').length > 0 && (
                                <Chip 
                                  label={`${Object.values(team.members).filter((m: any) => m.memberRole === 'Görevli').length} Görevli`} 
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                              {Object.values(team.members).filter((m: any) => m.memberRole === 'İzleyici').length > 0 && (
                                <Chip 
                                  label={`${Object.values(team.members).filter((m: any) => m.memberRole === 'İzleyici').length} İzleyici`} 
                                  size="small"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {team.createdAt ? formatDate(team.createdAt) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" color="primary" onClick={() => handleOpenTeamDetails(team)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => handleMenuOpen(e, team.id)}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={teams.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage={t('teamTasks.pagination.rowsPerPageTeam')}
                labelDisplayedRows={({ from, to, count }) => 
                  t('teamTasks.pagination.displayedRowsTeam', {
                    count: count,
                    from: from,
                    to: to
                  })}
                sx={{ mt: 2 }}
              />
            </>
          )}
        </>
      )}
      
      {/* Aksiyon Menüsü */}
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom'}}
      >
        <MenuItem onClick={externalDisplayMode === 'tasks' ? handleEditProject : handleEditTeam}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('teamTasks.menu.edit')}</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={externalDisplayMode === 'tasks' ? handleDeleteProject : handleDeleteTeam} 
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('teamTasks.menu.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Silme Onay Modalı */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setProjectSqlIdToDelete(null);
          setTeamSqlIdToDelete(null);
          setHasAssignedProjects(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {teamSqlIdToDelete && hasAssignedProjects 
            ? "Ekip Silinemez"
            : "Silme İşlemini Onayla"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {teamSqlIdToDelete && hasAssignedProjects 
              ? "Bu ekibe atanmış projeler bulunduğu için ekip silinemiyor. Öncelikle ekibe atanmış tüm projeleri silmeniz gerekmektedir."
              : "Bu öğeyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteConfirmOpen(false);
            setProjectSqlIdToDelete(null);
            setTeamSqlIdToDelete(null);
            setHasAssignedProjects(false);
          }}>{teamSqlIdToDelete && hasAssignedProjects 
            ? "Tamam"
            : "İptal"}</Button>
          {!(teamSqlIdToDelete && hasAssignedProjects) && (
            <Button onClick={confirmDeleteProject} color="error" autoFocus>
              {"Sil"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Snackbar Bildirimi */}
      <Snackbar 
        open={!!snackbar} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={snackbar?.severity || 'info'} sx={{ width: '100%' }} elevation={6} variant="filled">
          {snackbar?.message}
        </MuiAlert>
      </Snackbar>

      {/* Ekip Detayları Modalı */}
      <TeamDetailsModal
        open={teamDetailsModalOpen}
        onClose={() => setTeamDetailsModalOpen(false)}
        selectedTeam={selectedTeam}
        companyId={companyId}
        personnel={personnel}
        onSaved={triggerRefresh}
      />
      
      {/* Ekip Görevi Ekleme Modalı */}
      <CreateTeamTaskModal
        open={createTeamTaskModalOpen}
        onClose={handleCloseCreateModal}
        companyId={companyId}
        teams={teams}
        personnel={personnel}
        taskGroups={taskGroups}
        projectSqlIdToEdit={projectToEditSqlId}
        onSaved={triggerRefresh}
      />
      
      {/* Ekip Oluşturma/Düzenleme Modalı */}
      {createTeamModalOpen && (
        <CreateTeamModal
          open={createTeamModalOpen}
          onClose={() => setCreateTeamModalOpen(false)}
          companyId={companyId}
          personnel={personnel}
          teamSqlIdToEdit={teamToEditSqlId}
          onSaved={triggerRefresh}
        />
      )}
      
      {/* Proje Detayları Modalı */}
      <TeamTaskDetailModal
        open={projectDetailModalOpen}
        onClose={() => setProjectDetailModalOpen(false)}
        projectSqlId={selectedProjectSqlId}
        companyId={companyId || ''}
        onChanged={triggerRefresh}
      />
    </Box>
  );
};

export default TeamTasks; 